/**
 * AppLayout — the app shell around every page (replaces the old Bootstrap navbar,
 * containers/Header/header.js).
 *
 *   ┌──────────────────────────────────────────┬──────────┐
 *   │ top bar: page title ............ user ▾  │          │
 *   ├──────────────────────────────────────────┤  side    │
 *   │                                          │  menu    │
 *   │                page content              │ (right,  │
 *   │                                          │   RTL)   │
 *   └──────────────────────────────────────────┴──────────┘
 *
 * - Desktop: collapsible side menu (collapsed state remembered in localStorage).
 * - Mobile (< lg): menu moves into a slide-in drawer opened from the top bar.
 * - Behaviour kept from the old header:
 *     * auth === false  → opens the login modal (setShowLogIn(true))
 *     * URL contains "signin" → no chrome at all, just the page
 *     * "ניהול סטטוסי לקוח" opens the list editor (setShowListAdder(true, 'customerStatus'))
 *     * logout = GET /api/logout
 *     * <ListAdder/> (the list editor modal) is mounted here, as it was in the header
 * - The WhatsApp menu icon carries a status dot (green connected / amber connecting /
 *   red disconnected) from the shared poller in whatsappStatus.js.
 *
 * To add a page to the menu: add an entry to NAV_ITEMS (path must match a <Route> in App.js).
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Layout, Menu, Dropdown, Button, Drawer, Grid, Avatar } from 'antd';
import {
  TeamOutlined,
  MailOutlined,
  TagsOutlined,
  LineChartOutlined,
  WhatsAppOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
} from '@ant-design/icons';

import * as actionsCreators from '../actions/index.js';
import ListAdder from '../containers/updaters/listAdder/listAdder.js';
import { tokens } from './theme';
import { useWhatsappStatus } from './whatsappStatus';
import styles from './AppLayout.css';

const { Sider, Header, Content } = Layout;

// Side menu entries. `path` is both the menu key and the route.
export const NAV_ITEMS = [
  { path: '/', label: 'לקוחות', icon: <TeamOutlined /> },
  { path: '/emailAutomation', label: 'אוטומציית אימיילים', icon: <MailOutlined /> },
  { path: '/tagManager', label: 'ניהול טאגים', icon: <TagsOutlined /> },
  { path: '/eventTracking', label: 'מעקב אירועים', icon: <LineChartOutlined /> },
  { path: '/whatsapp', label: 'WhatsApp', icon: <WhatsAppOutlined /> },
  { path: '/cardcomConfig', label: 'ניהול Cardcom', icon: <CreditCardOutlined /> },
  { path: '/admin', label: 'לוח בקרה', icon: <DashboardOutlined /> },
  // AI assistant (/ai-insert) removed from the menu 2026-10-22 — not in use. The route still works by URL,
  // and its server code (service.aiInsert) also serves Business Hub (routes/hub.route.js), so keep it.
];

// Settings entries open the existing list editor (ListAdder) for a given list.
const SETTINGS_ITEMS = [{ key: 'customerStatus', label: 'ניהול סטטוסי לקוח' }];

const COLLAPSED_KEY = 'ui.sider.collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const AppLayout = (props) => {
  const { auth, location, history, children } = props;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const whatsapp = useWhatsappStatus();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Old header behaviour: not logged in → show the login modal.
  useEffect(() => {
    if (auth === false) props.setShowLogIn(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch (e) {
      /* storage unavailable — fine, just not remembered */
    }
  };

  // Sign-in pages render bare, like the old header returned null there.
  if (window.location.href.toLowerCase().includes('signin')) return children;

  const isLoggedIn = !!auth;
  const current = NAV_ITEMS.find((i) => i.path === location.pathname) || NAV_ITEMS[0];

  const menuItems = isLoggedIn
    ? [
        ...NAV_ITEMS.map((i) =>
          i.path === '/whatsapp'
            ? {
                key: i.path,
                // Icon + connection dot; the tooltip (title) names the state.
                icon: (
                  <span className={styles.iconWithDot} title={whatsapp.label}>
                    {i.icon}
                    <span className={styles.statusDot} style={{ background: whatsapp.color }} aria-label={whatsapp.label} />
                  </span>
                ),
                label: i.label,
              }
            : { key: i.path, icon: i.icon, label: i.label }
        ),
        { type: 'divider' },
        {
          key: 'settings',
          icon: <SettingOutlined />,
          label: 'הגדרות',
          children: SETTINGS_ITEMS.map((s) => ({ key: `settings:${s.key}`, label: s.label })),
        },
      ]
    : [];

  const onMenuClick = ({ key }) => {
    if (key.startsWith('settings:')) {
      props.setShowListAdder(true, key.split(':')[1]);
      return;
    }
    history.push(key);
  };

  const brand = (compact) => (
    <div className={styles.brand} onClick={() => history.push('/')}>
      <img src="/assets/logo.png" alt="logo" className={styles.logo} />
      {!compact && <span className={styles.brandName}>TalsCRM</span>}
    </div>
  );

  const menu = (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={onMenuClick}
      className={styles.menu}
    />
  );

  const userMenu = {
    items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'התנתק', danger: true }],
    onClick: ({ key }) => {
      if (key === 'logout') window.location.href = '/api/logout';
    },
  };

  const userName = auth && auth.name ? auth.name : '';

  // The element tree is identical on every screen size (the sider is only hidden on
  // mobile, the drawer only opened there). Swapping elements in and out remounted the
  // page — rotating a phone used to wipe the current search.
  return (
    <Layout className={styles.root} hasSider>
      <Sider
        width={tokens.layout.siderWidth}
        collapsedWidth={tokens.layout.siderCollapsedWidth}
        collapsed={collapsed}
        trigger={null}
        className={styles.sider}
        style={isMobile ? { display: 'none' } : undefined}
      >
        {brand(collapsed)}
        {menu}
        <div className={styles.siderFooter}>
          <Button
            type="text"
            icon={collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'הרחב תפריט' : 'כווץ תפריט'}
          />
        </div>
      </Sider>

      <Drawer
        placement="right"
        open={isMobile && mobileOpen}
        onClose={() => setMobileOpen(false)}
        width={tokens.layout.siderWidth + 24}
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        {brand(false)}
        {menu}
      </Drawer>

      <Layout>
        <Header className={styles.header}>
          <div className={styles.headerStart}>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined />} onClick={() => setMobileOpen(true)} aria-label="תפריט" />
            )}
            {/* The page title lives in each page's PageHeader; the bar only names
                the section on mobile, where the side menu is hidden. */}
            {isMobile && <span className={styles.pageTitle}>{isLoggedIn ? current.label : ''}</span>}
          </div>
          {isLoggedIn && (
            <Dropdown menu={userMenu} trigger={['click']} placement="bottomLeft">
              <Button type="text" className={styles.userButton}>
                <Avatar size={28} className={styles.avatar}>
                  {userName ? userName.trim().charAt(0) : '?'}
                </Avatar>
                {!isMobile && <span>{userName}</span>}
                <DownOutlined className={styles.userChevron} />
              </Button>
            </Dropdown>
          )}
        </Header>
        <Content className={styles.content}>{children}</Content>
      </Layout>

      {/* List editor modal, opened from the settings menu (was mounted in the old header). */}
      <ListAdder />
    </Layout>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default withRouter(connect(mapStateToProps, actionsCreators)(AppLayout));
